'use client';

import { createContext, useCallback, useContext, useState, useRef } from 'react';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { jobsApi } from '@/api/jobs';
import { useToast } from '@/hooks/use-toast';
import { isBackendUnavailableError } from '@/lib/backend-maintenance';
import { type Action } from '@/utils/constants';
import type {
  Job,
  CreateJobRequest,
  UpdateJobRequest,
  JobStatus,
  ScreeningQuestion,
  CustomInterview,
  FlattenedCandidate,
  InvitedApplicant,
  StageWithCandidates,
  CandidateDetails,
  ProcessedJobCandidate,
  VoiceConfig,
  ScreeningSchema,
  InterviewLanguage,
  ScreeningFormBatch,
  ScreeningPublishAction,
  ScreeningFormGenerateParams,
  JobCandidateListFilters,
} from '@/types/jobs';

interface PaginationState {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
}

interface JobsState {
  data: Job[];
  isLoading: boolean;
  error: string | null;
  pagination: PaginationState;
}

interface JobState {
  data: Job | null;
  isLoading: boolean;
  error: string | null;
}

interface EnhancedJobDescriptionState {
  data: string | null;
  isLoading: boolean;
  error: string | null;
}

interface ScreeningQuestionsState {
  data: ScreeningQuestion[];
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
}

interface ScreeningSchemaState {
  data: ScreeningSchema | null;
  isGenerating: boolean;
  isSaving: boolean;
  error: string | null;
  /**
   * Job id this schema belongs to (for modals that reuse global state across jobs)
   */
  jobId: string | null;
}

interface CustomInterviewState {
  data: CustomInterview | null;
  isLoading: boolean;
  isGenerating: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: string | null;
}

interface JobsContextType {
  jobs: JobsState;
  job: JobState;
  enhancedJobDescription: EnhancedJobDescriptionState;
  jobCandidates: {
    data: FlattenedCandidate[];
    isLoading: boolean;
    error: string | null;
    totalCount: number;
  };
  invitedApplicants: {
    data: InvitedApplicant[];
    isLoading: boolean;
    error: string | null;
    totalCount: number;
  };
  jobStages: {
    data: StageWithCandidates[];
    isLoading: boolean;
    error: string | null;
  };
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isUpdatingJobStatus: boolean;
  isUpdatingCandidateStatus: boolean;
  parsedJobData: {
    data: CreateJobRequest | null;
    isLoading: boolean;
    error: string | null;
  };
  screeningQuestions: ScreeningQuestionsState;
  screeningSchema: ScreeningSchemaState;
  customInterview: CustomInterviewState;
  customInterviewError: string | null;
  enhanceJobDescription: (description: string) => Promise<string>;
  autoFormatJobDescription: (description: string) => Promise<string>;
  fetchJobs: (page?: number, limit?: number) => Promise<void>;
  fetchJobById: (id: string) => Promise<void>;
  fetchJobCandidates: ({ 
    jobId,
    searchQuery,
    semanticQuery,
    filters,
  }: {
    jobId: string;
    searchQuery?: string;
    semanticQuery?: string;
    /** Pass `null` to clear filters; omit to keep cached filters on refetch. */
    filters?: JobCandidateListFilters | null;
  }) => Promise<void>;
  clearJobCandidatesSearch: (jobId: string) => void;
  createJob: (data: CreateJobRequest) => Promise<Job>;
  updateJob: (data: UpdateJobRequest) => Promise<void>;
  deleteJob: (id: string) => Promise<void>;
  updateJobStatus: (id: string, status: JobStatus) => Promise<void>;
  updateJobVoiceConfig: (id: string, voiceConfig: VoiceConfig) => Promise<void>;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  updateJobCandidateStatus: (
    jobId: string,
    candidateId: string,
    targetStageId: string,
    sendRejectionEmail?: boolean
  ) => Promise<void>;
  parseJobDescriptionFile: (file: File) => Promise<CreateJobRequest>;
  parseJobDescriptionText: (jobDescription: string) => Promise<CreateJobRequest>;
  parseJobPrompt: (prompt: string) => Promise<CreateJobRequest>;
  setParsedJobData: (data: CreateJobRequest | null) => void;
  generateScreeningQuestions: (jobId: string, params?: { questionDump?: string }) => Promise<void>;
  generateScreeningSchema: (
    jobId: string,
    params?: ScreeningFormGenerateParams
  ) => Promise<ScreeningSchema>;
  generateScreeningSchemaFromDraft: (
    jobData: { title: string; description: string; skills?: string[] },
    params?: ScreeningFormGenerateParams
  ) => Promise<ScreeningSchema>;
  saveScreeningSchema: (
    jobId: string,
    schema: ScreeningSchema | null,
    publishAction: ScreeningPublishAction
  ) => Promise<void>;
  screeningFormBatches: ScreeningFormBatch[];
  fetchScreeningFormBatches: (jobId: string) => Promise<void>;
  submitScreeningResponses: (jobId: string, candidateId: string, responses: Record<string, string | number | string[]>) => Promise<void>;
  updateScreeningQuestions: (jobId: string, questions: ScreeningQuestion[], criteria?: string[]) => Promise<void>;
  clearScreeningQuestionsError: () => void;
  clearScreeningQuestions: () => void;
  generateCustomInterview: (jobId: string) => Promise<void>;
  createCustomInterview: (jobId: string, customInterview: CustomInterview) => Promise<void>;
  updateCustomInterview: (jobId: string, customInterview: CustomInterview) => Promise<void>;
  clearCustomInterviewError: () => void;
  clearCustomInterview: () => void;
  generateUnifiedInterview: (
    jobId: string,
    params: { questionConfig?: { screening?: number; technical?: number; behavioural?: number; cultural?: number; background?: number }; additionalPrompt?: string; backgroundFocusDescription?: string }
  ) => Promise<void>;
  createUnifiedInterview: (jobId: string, customInterview: CustomInterview) => Promise<void>;
  updateUnifiedInterview: (jobId: string, customInterview: CustomInterview) => Promise<void>;
  exportCandidates: (jobId: string, pipelineStageId?: string, jobTitle?: string) => Promise<void>;
  updateStages: (
    jobId: string,
    stages: Array<{ id?: string; name: string; order: number; action: Action }>
  ) => Promise<void>;
}

const JobsContext = createContext<JobsContextType | undefined>(undefined);

export function JobsProvider({ children }: { children: React.ReactNode }) {
  const { organisation } = useOrganisation();
  
  // Cache for search results - key: `${jobId}:${searchQuery}:${semanticQuery}`, value: { data, timestamp }
  const searchCache = useRef(new Map<string, { data: any; timestamp: number }>());
  const CACHE_TTL = 2 * 60 * 1000; // 2 minutes in milliseconds
  const CACHE_CLEANUP_THRESHOLD = 5 * 60 * 1000; // Clean up entries older than 5 minutes

  // Persist last search per job so refetches (e.g. after moving candidate) keep the same search
  const lastFetchParamsByJobIdRef = useRef<
    Record<string, { searchQuery?: string; semanticQuery?: string; filters?: JobCandidateListFilters }>
  >({});
  
  const [jobs, setJobs] = useState<JobsState>({
    data: [],
    isLoading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      total: 0,
      limit: 10,
    },
  });

  const [job, setJob] = useState<JobState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingCandidateStatus, setIsUpdatingCandidateStatus] = useState(false);
  const [isUpdatingJobStatus, setIsUpdatingJobStatus] = useState(false);
  const [enhancedJobDescription, setEnhancedJobDescription] = useState<EnhancedJobDescriptionState>(
    {
      data: null,
      isLoading: false,
      error: null,
    }
  );
  const [jobCandidates, setJobCandidates] = useState<{
    data: FlattenedCandidate[];
    isLoading: boolean;
    error: string | null;
    totalCount: number;
    pagination?: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
    };
  }>({
    data: [],
    isLoading: false,
    error: null,
    totalCount: 0,
  });
  const [invitedApplicants, setInvitedApplicants] = useState<{
    data: InvitedApplicant[];
    isLoading: boolean;
    error: string | null;
    totalCount: number;
  }>({
    data: [],
    isLoading: false,
    error: null,
    totalCount: 0,
  });
  const [jobStages, setJobStages] = useState<{
    data: StageWithCandidates[];
    isLoading: boolean;
    error: string | null;
  }>({
    data: [],
    isLoading: false,
    error: null,
  });
  const [parsedJobData, setParsedJobData] = useState<{
    data: CreateJobRequest | null;
    isLoading: boolean;
    error: string | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
  });
  const [screeningQuestions, setScreeningQuestions] = useState<ScreeningQuestionsState>({
    data: [],
    isLoading: false,
    error: null,
    isGenerating: false,
  });
  const [screeningSchema, setScreeningSchema] = useState<ScreeningSchemaState>({
    data: null,
    isGenerating: false,
    isSaving: false,
    error: null,
    jobId: null,
  });
  const [screeningFormBatches, setScreeningFormBatches] = useState<ScreeningFormBatch[]>([]);
  const [customInterview, setCustomInterview] = useState<CustomInterviewState>({
    data: null,
    isLoading: false,
    error: null,
    isGenerating: false,
    isCreating: false,
    isUpdating: false,
  });
  const { toast } = useToast();

  const fetchJobs = useCallback(
    async (page?: number, limit?: number) => {
      // Use functional state update to get current pagination values
      // This avoids circular dependency issues
      let targetPage: number;
      let targetLimit: number;

      setJobs(prev => {
        targetPage = page ?? prev.pagination.currentPage;
        targetLimit = limit ?? prev.pagination.limit;
        return { ...prev, isLoading: true };
      });

      try {
        const response = await jobsApi.listJobs(targetPage!, targetLimit!);
        setJobs({
          data: response.jobs,
          isLoading: false,
          error: null,
          pagination: {
            currentPage: response.pagination.currentPage,
            totalPages: response.pagination.totalPages,
            total: response.pagination.total,
            limit: response.pagination.limit,
          },
        });
      } catch (error) {
        setJobs(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load jobs',
        }));
        if (!isBackendUnavailableError(error)) {
          toast({
            description: 'Failed to load jobs',
            variant: 'error',
          });
        }
      }
    },
    [toast] // Only depend on toast, not on jobs state to prevent infinite loops
  );

  const setPage = useCallback(
    (page: number) => {
      fetchJobs(page, jobs.pagination.limit);
    },
    [jobs.pagination.limit, fetchJobs]
  );

  const setLimit = useCallback(
    (limit: number) => {
      fetchJobs(1, limit); // Reset to first page when changing limit
    },
    [fetchJobs]
  );

  const createJob = useCallback(
    async (data: CreateJobRequest): Promise<Job> => {
      setIsCreating(true);
      try {
        const response = await jobsApi.createJob(data);

        toast({ description: 'Job created successfully' });

        return response.job;
      } catch (error) {
        toast({
          description: error instanceof Error ? error.message : 'Failed to create job',
          variant: 'error',
        });
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [toast]
  );

  const updateJob = useCallback(
    async (data: UpdateJobRequest) => {
      setIsUpdating(true);
      try {
        await jobsApi.updateJob(data);
        await fetchJobs(); // Refresh the list after update
        toast({ description: 'Job updated successfully' });

        // Fire revalidation for careers pages
        try {
          const orgId = organisation.data?.id;
          if (orgId) {
            await fetch('/api/revalidate-proxy', {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                orgId,
                jobId: data.id,
                path: `/careers/${orgId}/jobs/${data.id}`,
                tags: [
                  `job:${data.id}`,
                  `org:${orgId}:jobs`,
                  `org:${orgId}`,
                  'jobs',
                  'organisations',
                ],
              }),
            });
          }
        } catch (err) {
          console.error('Revalidation proxy failed', err);
        }
      } catch (error) {
        toast({
          description: error instanceof Error ? error.message : 'Failed to update job',
          variant: 'error',
        });
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchJobs, toast, organisation.data?.id]
  );

  const deleteJob = useCallback(
    async (id: string) => {
      setIsDeleting(true);
      try {
        await jobsApi.deleteJob(id);
        await fetchJobs(); // Refresh the list after deletion
        toast({ description: 'Job deleted successfully' });
      } catch (error) {
        toast({
          description: error instanceof Error ? error.message : 'Failed to delete job',
          variant: 'error',
        });
        throw error;
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchJobs, toast]
  );

  const updateJobStatus = useCallback(
    async (id: string, status: JobStatus) => {
      setIsUpdatingJobStatus(true);
      try {
        await jobsApi.updateJobStatus(id, status);
        await fetchJobs();
        toast({ description: 'Job status updated successfully' });

        // Fire revalidation for careers pages
        try {
          const orgId = organisation.data?.id;
          if (orgId) {
            await fetch('/api/revalidate-proxy', {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                orgId,
                jobId: id,
                path: `/careers/${orgId}/jobs/${id}`,
                tags: [
                  `job:${id}`,
                  `org:${orgId}:jobs`,
                  `org:${orgId}`,
                  'jobs',
                  'organisations',
                ],
              }),
            });
          }
        } catch (err) {
          console.error('Revalidation proxy failed', err);
        }
      } catch (error) {
        toast({
          description: error instanceof Error ? error.message : 'Failed to update job status',
          variant: 'error',
        });
        throw error;
      } finally {
        setIsUpdatingJobStatus(false);
      }
    },
    [toast, organisation.data?.id, fetchJobs]
  );

  // Helper functions for clearing states
  const clearScreeningQuestions = useCallback(() => {
    setScreeningQuestions({
      data: [],
      isLoading: false,
      error: null,
      isGenerating: false,
    });
  }, []);

  const clearScreeningQuestionsError = useCallback(() => {
    setScreeningQuestions(prev => ({ ...prev, error: null }));
  }, []);

  const fetchJobById = useCallback(
    async (id: string) => {
      setJob(prev => ({ ...prev, isLoading: true }));
      // Clear any existing screening questions when switching jobs
      clearScreeningQuestions();
      try {
        const response = await jobsApi.getJob(id);
        setJob({
          data: response.job,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setJob(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load job',
        }));
        toast({
          description: 'Failed to load job details',
          variant: 'error',
        });
      }
    },
    [toast, clearScreeningQuestions]
  );

  const updateJobVoiceConfig = useCallback(
    async (id: string, voiceConfig: VoiceConfig) => {
      try {
        // Ensure language is present for API contract (fallback to existing or 'en')
        const safeVoiceConfig = {
          ...voiceConfig,
          language: voiceConfig.language || ('en' as InterviewLanguage),
        };
        await jobsApi.updateJobVoiceConfig(id, safeVoiceConfig);
        // Refresh the job list to reflect the change
        await fetchJobs();
        // If we have the job in state, update it
        if (job.data?.id === id) {
          await fetchJobById(id);
        }
        toast({ description: 'Voice configuration updated successfully' });
      } catch (error) {
        toast({
          description: error instanceof Error ? error.message : 'Failed to update voice configuration',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast, fetchJobs, fetchJobById, job.data?.id]
  );

  const enhanceJobDescription = useCallback(
    async (description: string) => {
      setEnhancedJobDescription(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await jobsApi.enhanceJobDescription(description);
        setEnhancedJobDescription({
          data: response.jobDescription,
          isLoading: false,
          error: null,
        });
        toast({ description: 'Job description enhanced successfully' });
        return response.jobDescription || '';
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to enhance job description';
        setEnhancedJobDescription(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        throw error;
      }
    },
    [toast]
  );

  const autoFormatJobDescription = useCallback(
    async (description: string) => {
      setEnhancedJobDescription(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await jobsApi.autoFormatJobDescription(description);
        setEnhancedJobDescription({
          data: response.jobDescription,
          isLoading: false,
          error: null,
        });
        toast({ description: 'Job description formatted successfully' });
        return response.jobDescription || '';
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to format job description';
        setEnhancedJobDescription(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        throw error;
      }
    },
    [toast]
  );

  const clearJobCandidatesSearch = useCallback((jobId: string) => {
    delete lastFetchParamsByJobIdRef.current[jobId];
  }, []);

  const normalizeListFilters = (
    filters: JobCandidateListFilters | null | undefined
  ): JobCandidateListFilters | undefined => {
    if (filters == null) return undefined;
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const fetchJobCandidates = useCallback(
    async ({ jobId, searchQuery, semanticQuery, filters }: Parameters<JobsContextType['fetchJobCandidates']>[0]) => {
      const cached = lastFetchParamsByJobIdRef.current[jobId];
      const hasExplicitSearch = searchQuery !== undefined || semanticQuery !== undefined;
      const hasExplicitFilters = filters !== undefined;
      const hasExplicitParams = hasExplicitSearch || hasExplicitFilters;

      let effectiveSearchQuery: string | undefined;
      let effectiveSemanticQuery: string | undefined;
      let effectiveFilters: JobCandidateListFilters | undefined;

      if (hasExplicitSearch) {
        effectiveSearchQuery = searchQuery?.trim() || undefined;
        effectiveSemanticQuery = semanticQuery?.trim() || undefined;
        effectiveFilters = hasExplicitFilters
          ? normalizeListFilters(filters)
          : cached?.filters;
      } else if (hasExplicitFilters) {
        effectiveSearchQuery = cached?.searchQuery;
        effectiveSemanticQuery = cached?.semanticQuery;
        effectiveFilters = normalizeListFilters(filters);
      } else if (cached) {
        effectiveSearchQuery = cached.searchQuery;
        effectiveSemanticQuery = cached.semanticQuery;
        effectiveFilters = cached.filters;
      }

      if (hasExplicitParams) {
        lastFetchParamsByJobIdRef.current[jobId] = {
          searchQuery: effectiveSearchQuery,
          semanticQuery: effectiveSemanticQuery,
          filters: effectiveFilters,
        };
      }

      setJobCandidates(prev => ({ ...prev, isLoading: true, error: null }));
      setInvitedApplicants(prev => ({ ...prev, isLoading: true, error: null }));
      setJobStages(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await jobsApi.getJobCandidates(
          jobId,
          effectiveSearchQuery,
          effectiveSemanticQuery,
          effectiveFilters
        );
        
        // handleAPIResponse already extracts .data, so response is the data object
        // Check if response has the expected structure
        if (!response || !response.stages) {
          throw new Error('Invalid API response structure');
        }

        // Flatten candidates from all stages and add pipelineStageId
        // Preserve all fields from the backend, including any additional fields like screeningResults
        const allCandidatesFlattened: FlattenedCandidate[] = response.stages.flatMap(stage =>
          stage.candidates.map(processedJobCandidate => {
            // Debug: Log the structure of processedJobCandidate to see what fields are available
            // Create a copy of all fields from processedJobCandidate (excluding nested objects we handle separately)
            const { candidate, stage: stageInfo, id, ...otherFields } = processedJobCandidate;
            
            return {
              ...candidate, // Spread all candidate details first
              ...otherFields, // Spread any additional fields from the job-candidate association
              // Normalize interest flag from snake_case or camelCase on the association
              interestedInJob:
                (otherFields as Record<string, unknown>).interestedInJob as boolean | undefined ??
                (otherFields as Record<string, unknown>).interested_in_job as boolean | undefined ??
                ('interestedInJob' in candidate ? (candidate as { interestedInJob?: boolean }).interestedInJob : undefined),
              // Explicitly preserve scheduledRejectionEmail from otherFields
              scheduledRejectionEmail: (otherFields as Record<string, unknown>).scheduledRejectionEmail as { taskId: string; scheduledAt: string | Date; status: 'PENDING' | 'PROCESSING' } | null | undefined,
              // Ensure the candidate's actual ID is preserved, not the job-candidate association ID
              id: candidate.id, // Explicitly preserve the candidate's ID
              pipelineStageId: stage.id,
              jobCandidateId: processedJobCandidate.id, // The job-candidate association ID
              createdAt: processedJobCandidate.createdAt, // Ensure createdAt is from the job-candidate association
            } as FlattenedCandidate;
          })
        );

        setJobCandidates({
          data: allCandidatesFlattened,
          isLoading: false,
          error: null,
          totalCount: response.totalCandidatesCount || 0,
        });

        // Set invited applicants if they exist in the response
        setInvitedApplicants({
          data: response.invitedApplicants || [],
          isLoading: false,
          error: null,
          totalCount: response.totalApplicantsCount || 0,
        });

        // Always keep stages sorted by 'order' to avoid tab order flicker
        const sortedStages = [...response.stages].sort((a, b) => a.order - b.order);
        // Normalize interestedInJob onto nested candidate objects for consistent UI
        const normalizedStages = sortedStages.map((stage) => ({
          ...stage,
          // Be defensive: some API responses or mutations may omit candidates
          candidates: Array.isArray(stage.candidates)
            ? stage.candidates.map((processedCandidate: ProcessedJobCandidate) => ({
                ...processedCandidate,
                candidate: {
                  ...processedCandidate.candidate,
                  interestedInJob:
                    processedCandidate.interestedInJob ??
                    (processedCandidate as { interested_in_job?: boolean }).interested_in_job,
                },
              }))
            : [],
        }));
        setJobStages({
          data: normalizedStages as StageWithCandidates[],
          isLoading: false,
          error: null,
        });

        if (
          Array.isArray(response.screeningSchema) &&
          response.screeningSchema.length > 0
        ) {
          setJob((prev) => {
            if (!prev.data || String(prev.data.id) !== String(jobId)) return prev;
            return {
              ...prev,
              data: { ...prev.data, screeningSchema: response.screeningSchema },
            };
          });
        }
      } catch (error) {
        console.error('Error in fetchJobCandidates:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load job candidates';
        setJobCandidates(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          totalCount: 0,
        }));
        setInvitedApplicants(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          totalCount: 0,
        }));
        setJobStages(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          description: 'Failed to load job candidates',
          variant: 'error',
        });
      }
    },
    [toast]
  );

  /**
   * Move a candidate to a different pipeline stage
   * Uses optimistic UI updates for immediate feedback
   */
  const updateJobCandidateStatus = useCallback(
    async (jobId: string, candidateId: string, targetStageId: string, sendRejectionEmail?: boolean) => {
      setIsUpdatingCandidateStatus(true);
      try {
        // 1. Update candidates list with optimistic update for immediate UI feedback
        setJobCandidates(prevState => ({
          ...prevState,
          data: prevState.data.map(candidate =>
            String(candidate.id) === candidateId ? 
              { ...candidate, pipelineStageId: targetStageId } : 
              candidate
          ),
        }));
        
        // 2. Update pipeline stages UI with optimistic update
        setJobStages(prevState => {
          // Create a deep copy to avoid mutation issues while preserving types.
          // Be defensive: some stages may not have a candidates array (e.g. after stage updates).
          const updatedStages: StageWithCandidates[] = prevState.data.map(stage => ({
            ...stage,
            candidates: Array.isArray(stage.candidates) ? [...stage.candidates] : [],
          }));
          
          // Find the candidate in their current stage
          let candidateData: CandidateDetails | null = null;
          
          // First pass: find and remove the candidate from their current stage
          updatedStages.forEach((stage) => {
            const candidateIndex = stage.candidates.findIndex((processedCandidate) => String(processedCandidate.candidate.id) === candidateId);
            if (candidateIndex >= 0) {
              candidateData = stage.candidates[candidateIndex].candidate;
              stage.candidates.splice(candidateIndex, 1);
            }
          });
          
          // Second pass: add the candidate to the target stage
          if (candidateData) {
            const cd = candidateData as CandidateDetails;
            const targetStage = updatedStages.find((stage) => stage.id === targetStageId);
            if (targetStage) {
              // Create a new ProcessedJobCandidate object
              const optimistic: ProcessedJobCandidate = {
                id: `temp-${Date.now()}`,
                jobId,
                candidateId: String(cd.id),
                stageId: targetStageId,
                status: 'ACTIVE',
                meta: null,
                resumeMatchScore: null,
                resumeMatchAnalysis: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                applicationDate: new Date().toISOString(),
                candidate: candidateData,
                stage: {
                  id: targetStageId,
                  name: targetStage.name,
                  order: targetStage.order,
                  isDefault: targetStage.isDefault,
                  defaultStageType: targetStage.defaultStageType,
                },
              };
              targetStage.candidates.push(optimistic);
            }
          }
          
          return {
            ...prevState,
            data: updatedStages
          };
        });
        
        // 3. Make the API call to persist the change
        await jobsApi.updateCandidateStatus(jobId, candidateId, targetStageId, sendRejectionEmail);
        
        // 4. Refresh data in the background for eventual consistency
        fetchJobCandidates({ jobId }).catch(err => {
          console.error('Error refreshing candidates:', err);
        });
      } catch (error) {
        // Revert optimistic updates on error by fetching fresh data
        fetchJobCandidates({ jobId }).catch(console.error);
        
        toast({
          description: error instanceof Error ? error.message : 'Failed to update candidate status',
          variant: 'error',
        });
        throw error;
      } finally {
        setIsUpdatingCandidateStatus(false);
      }
    },
    [setJobCandidates, setJobStages, jobsApi, fetchJobCandidates, toast]
  );

  const parseJobDescriptionFile = useCallback(
    async (file: File) => {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        throw new Error('File size should not exceed 10MB');
      }

      const fileType = file.type.toLowerCase();
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      if (!allowedTypes.includes(fileType)) {
        throw new Error('Only PDF, DOC, and DOCX files are allowed');
      }

      setParsedJobData(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await jobsApi.parseJobDescriptionFile(file);
        setParsedJobData({
          data: response.job,
          isLoading: false,
          error: null,
        });
        toast({ description: 'Job description parsed successfully' });
        return response.job;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to parse job description';
        setParsedJobData(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          description: errorMessage,
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const parseJobDescriptionText = useCallback(
    async (jobDescription: string) => {
      setParsedJobData(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await jobsApi.parseJobDescriptionText(jobDescription);
        setParsedJobData({
          data: response.job,
          isLoading: false,
          error: null,
        });
        toast({ description: 'Job description parsed successfully' });
        return response.job;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to parse job description';
        setParsedJobData(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          description: errorMessage,
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const parseJobPrompt = useCallback(
    async (prompt: string) => {
      setParsedJobData(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await jobsApi.parseJobPrompt(prompt);
        setParsedJobData({
          data: response.job,
          isLoading: false,
          error: null,
        });
        toast({ description: 'Job prompt parsed successfully' });
        return response.job;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to parse job prompt';
        setParsedJobData(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        toast({
          description: errorMessage,
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const generateScreeningQuestions = useCallback(
    async (jobId: string, params?: { questionDump?: string }) => {
      setScreeningQuestions(prev => ({
        ...prev,
        isLoading: true,
        error: null,
        isGenerating: true,
      }));
      try {
        const response = await jobsApi.generateScreeningQuestions(jobId, params);
        if (!response.screeningQuestions) {
          throw new Error('Failed to generate screening questions');
        }
        setScreeningQuestions({
          data: response.screeningQuestions,
          isLoading: false,
          error: null,
          isGenerating: false,
        });
      } catch (error) {
        setScreeningQuestions(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to generate screening questions',
          isGenerating: false,
        }));
        toast({
          description: 'Failed to generate screening questions',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const generateScreeningSchema = useCallback(
    async (
      jobId: string,
      params?: ScreeningFormGenerateParams
    ): Promise<ScreeningSchema> => {
      setScreeningSchema(prev => ({
        ...prev,
        isGenerating: true,
        error: null,
      }));
      try {
        const response = await jobsApi.generateScreeningSchema(jobId, params);
        setScreeningSchema({
          data: response.schema,
          isGenerating: false,
          isSaving: false,
          error: null,
          jobId,
        });
        toast({ description: 'Screening form generated' });
        return response.schema;
      } catch (error) {
        setScreeningSchema(prev => ({
          ...prev,
          isGenerating: false,
          error: error instanceof Error ? error.message : 'Failed to generate screening form',
        }));
        toast({
          description: 'Failed to generate screening form',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const generateScreeningSchemaFromDraft = useCallback(
    async (
      jobData: { title: string; description: string; skills?: string[] },
      params?: ScreeningFormGenerateParams
    ): Promise<ScreeningSchema> => {
      try {
        const response = await jobsApi.generateScreeningSchemaFromContext(jobData, params);
        toast({ description: 'Screening form generated' });
        return response.schema;
      } catch (error) {
        toast({
          description: 'Failed to generate screening form',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const saveScreeningSchema = useCallback(
    async (jobId: string, schema: ScreeningSchema | null, publishAction: ScreeningPublishAction) => {
      setScreeningSchema(prev => ({ ...prev, isSaving: true, error: null }));
      try {
        if (!schema || schema.length === 0) {
          throw new Error('Screening form must have at least one question');
        }
        await jobsApi.createScreeningForm(jobId, schema, publishAction);
        setScreeningSchema(prev => ({
          ...prev,
          isSaving: false,
          data: schema,
          jobId,
        }));
        toast({
          description:
            publishAction === 'notify_existing' ? 'Notifying candidates' : 'Saved',
        });
      } catch (error) {
        setScreeningSchema(prev => ({
          ...prev,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to save screening form',
        }));
        toast({
          description: 'Failed to save screening form',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const fetchScreeningFormBatches = useCallback(
    async (jobId: string) => {
      try {
        const batches = await jobsApi.listScreeningForms(jobId);
        setScreeningFormBatches(batches);
      } catch (error) {
        setScreeningFormBatches([]);
        toast({
          description: 'Failed to load screening form versions',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const submitScreeningResponses = useCallback(
    async (jobId: string, candidateId: string, responses: Record<string, string | number | string[]>) => {
      try {
        await jobsApi.submitScreeningForm(jobId, candidateId, responses);
        toast({ description: 'Screening responses submitted' });
      } catch (error) {
        toast({
          description: error instanceof Error ? error.message : 'Failed to submit responses',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const updateScreeningQuestions = useCallback(
    async (jobId: string, questions: ScreeningQuestion[], criteria?: string[]) => {
      setScreeningQuestions(prev => ({ ...prev, isLoading: true, error: null }));
      try {
        await jobsApi.updateJob({
          id: jobId,
          screeningQuestions: questions,
          screeningQuestionsQuantitativeEvaluationCriteria: criteria,
        });
        // Clear global state after successful save
        clearScreeningQuestions();
        // Update the job state to reflect the changes
        setJob(prev => ({
          ...prev,
          data: prev.data ? { 
            ...prev.data, 
            screeningQuestions: questions,
            screeningQuestionsQuantitativeEvaluationCriteria: criteria
          } : null,
        }));
        toast({ description: 'Screening questions updated successfully' });
      } catch (error) {
        setScreeningQuestions(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to update screening questions',
        }));
        toast({
          description:
            error instanceof Error ? error.message : 'Failed to update screening questions',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast, clearScreeningQuestions]
  );

  const generateCustomInterview = useCallback(
    async (jobId: string) => {
      setCustomInterview(prev => ({ ...prev, isLoading: true, error: null, isGenerating: true }));
      try {
        const response = await jobsApi.generateCustomInterview(jobId);
        setCustomInterview({
          data: response.customInterview || null,
          isLoading: false,
          error: null,
          isGenerating: false,
          isCreating: false,
          isUpdating: false,
        });
      } catch (error) {
        setCustomInterview(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to generate custom interview',
          isGenerating: false,
        }));
        toast({
          description: 'Failed to generate custom interview',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast]
  );

  const createCustomInterview = useCallback(
    async (jobId: string, customInterview: CustomInterview) => {
      setCustomInterview(prev => ({ ...prev, isLoading: true, error: null, isCreating: true }));
      try {
        const response = await jobsApi.createCustomInterview(jobId, customInterview);
        setCustomInterview({
          data: response.customInterview || null,
          isLoading: false,
          error: null,
          isCreating: false,
          isGenerating: false,
          isUpdating: false,
        });

        // Refetch the job to update the job data with the new custom interview
        await fetchJobById(jobId);
      } catch (error) {
        setCustomInterview(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to create custom interview',
          isCreating: false,
        }));
        toast({
          description: 'Failed to create custom interview',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast, fetchJobById]
  );

  const updateCustomInterview = useCallback(
    async (jobId: string, customInterview: CustomInterview) => {
      setCustomInterview(prev => ({ ...prev, isLoading: true, error: null, isUpdating: true }));
      try {
        const response = await jobsApi.updateCustomInterview(jobId, customInterview);
        setCustomInterview({
          data: response.customInterview || null,
          isLoading: false,
          error: null,
          isUpdating: false,
          isGenerating: false,
          isCreating: false,
        });

        // Refetch the job to update the job data with the updated custom interview
        await fetchJobById(jobId);
      } catch (error) {
        setCustomInterview(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to update custom interview',
          isUpdating: false,
        }));
        toast({
          description: 'Failed to update custom interview',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast, fetchJobById]
  );

  const clearCustomInterviewError = useCallback(() => {
    setCustomInterview(prev => ({ ...prev, error: null }));
  }, []);

  const clearCustomInterview = useCallback(() => {
    setCustomInterview({
      data: null,
      isLoading: false,
      error: null,
      isGenerating: false,
      isCreating: false,
      isUpdating: false,
    });
  }, []);

  const generateUnifiedInterview = useCallback(
    async (
      jobId: string,
      params: { questionConfig?: { screening?: number; technical?: number; behavioural?: number; cultural?: number; background?: number }; additionalPrompt?: string; backgroundFocusDescription?: string }
    ) => {
      setCustomInterview(prev => ({ ...prev, isLoading: true, error: null, isGenerating: true }));
      try {
        const response = await jobsApi.generateUnifiedInterview(jobId, params);
        const unified = response.unifiedInterview;
        const mapped: CustomInterview = {
          title: 'AI Interview',
          questions: unified.questions,
          quantitativeAnalysisCriteria: Array.isArray(unified.evaluationCriteria)
            ? unified.evaluationCriteria.map(c => c.name)
            : [],
          evaluationCriteria: Array.isArray(unified.evaluationCriteria)
            ? unified.evaluationCriteria
            : [],
        };
        setCustomInterview({
          data: mapped,
          isLoading: false,
          error: null,
          isGenerating: false,
          isCreating: false,
          isUpdating: false,
        });
      } catch (error) {
        setCustomInterview(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to generate unified interview',
          isGenerating: false,
        }));
        toast({ description: 'Failed to generate AI interview', variant: 'error' });
        throw error;
      }
    },
    [toast]
  );

  const createUnifiedInterview = useCallback(
    async (jobId: string, unified: CustomInterview) => {
      setCustomInterview(prev => ({ ...prev, isLoading: true, error: null, isCreating: true }));
      try {
        const response = await jobsApi.createUnifiedInterview(jobId, unified);
        setCustomInterview({
          data: response.customInterview || null,
          isLoading: false,
          error: null,
          isCreating: false,
          isGenerating: false,
          isUpdating: false,
        });
        await fetchJobById(jobId);
      } catch (error) {
        setCustomInterview(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to create unified interview',
          isCreating: false,
        }));
        toast({ description: 'Failed to create AI interview', variant: 'error' });
        throw error;
      }
    },
    [toast, fetchJobById]
  );

  const updateUnifiedInterview = useCallback(
    async (jobId: string, unified: CustomInterview) => {
      setCustomInterview(prev => ({ ...prev, isLoading: true, error: null, isUpdating: true }));
      try {
        const response = await jobsApi.updateUnifiedInterview(jobId, unified);
        setCustomInterview({
          data: response.customInterview || null,
          isLoading: false,
          error: null,
          isUpdating: false,
          isGenerating: false,
          isCreating: false,
        });
        await fetchJobById(jobId);
      } catch (error) {
        setCustomInterview(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to update unified interview',
          isUpdating: false,
        }));
        toast({ description: 'Failed to update AI interview', variant: 'error' });
        throw error;
      }
    },
    [toast, fetchJobById]
  );

  const exportCandidates = useCallback(async (jobId: string, pipelineStageId?: string, jobTitle?: string) => {
    try {
      const { csvData, filename } = await jobsApi.exportCandidates(jobId, pipelineStageId, jobTitle);
      
      // Create download link
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        description: `Successfully exported candidates as ${filename}`,
        variant: 'success',
      });
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to export candidates',
        variant: 'error',
      });
      throw error;
    }
  }, [toast]);

  const updateStages = useCallback(async (
    jobId: string,
    stages: Array<{ id?: string; name: string; order: number; action: Action }>
  ) => {
    try {
      const response = await jobsApi.updatePipelineStages(jobId, stages);
      // Refresh job and candidates to reflect new stage setup
      await Promise.all([
        fetchJobById(jobId),
        fetchJobCandidates({ jobId }),
      ]);
      // Also update jobStages state immediately if returned
      if (response?.stages) {
        const sorted = [...response.stages].sort((a, b) => a.order - b.order) as StageWithCandidates[];
        setJobStages({ data: sorted, isLoading: false, error: null });
      }
      toast({ description: 'Stages updated successfully' });
    } catch (error) {
      toast({ description: error instanceof Error ? error.message : 'Failed to update stages', variant: 'error' });
      throw error;
    }
  }, [toast, fetchJobById, fetchJobCandidates]);

  return (
    <JobsContext.Provider
      value={{
        jobs,
        job,
        enhancedJobDescription,
        jobCandidates,
        invitedApplicants,
        jobStages,
        isCreating,
        isUpdating,
        isDeleting,
        isUpdatingJobStatus,
        isUpdatingCandidateStatus,
        parsedJobData,
        screeningQuestions,
        screeningSchema,
        customInterview,
        customInterviewError: null,
        enhanceJobDescription,
        autoFormatJobDescription,
        fetchJobs,
        fetchJobById,
        fetchJobCandidates,
        clearJobCandidatesSearch,
        createJob,
        updateJob,
        deleteJob,
        updateJobStatus,
        updateJobVoiceConfig,
        setPage,
        setLimit,
        updateJobCandidateStatus,
        parseJobDescriptionFile,
        parseJobDescriptionText,
        parseJobPrompt,
        generateScreeningQuestions,
        generateScreeningSchema,
        generateScreeningSchemaFromDraft,
        saveScreeningSchema,
        screeningFormBatches,
        fetchScreeningFormBatches,
        submitScreeningResponses,
        updateScreeningQuestions,
        clearScreeningQuestionsError,
        clearScreeningQuestions,
        generateCustomInterview,
        createCustomInterview,
        updateCustomInterview,
        clearCustomInterviewError,
        clearCustomInterview,
        generateUnifiedInterview,
        createUnifiedInterview,
        updateUnifiedInterview,
        exportCandidates,
        updateStages,
        setParsedJobData: (data: CreateJobRequest | null) => {
          setParsedJobData({
            data,
            isLoading: false,
            error: null,
          });
        },
      }}
    >
      {children}
    </JobsContext.Provider>
  );
}

export function useJobs() {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error('useJobs must be used within JobsProvider');
  }
  return context;
}
